import React, { useState } from "react";
import {
  Modal, Form, Select, Input, DatePicker, Rate, Slider, Button,
  Radio, message, Divider, Tag
} from "antd";
import { ExclamationCircleOutlined, WarningOutlined, StarOutlined, MessageOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../const";
import dayjs from "dayjs";

const { Option } = Select;
const { TextArea } = Input;

const RATING_BUCKET_SCORE = {
  need_more_time: 20,
  not_meeting: 30,
  meets_expectations: 50,
  impressive: 80,
  excellent: 100,
};

const RATING_BUCKET_LABEL = {
  need_more_time: "Need More Time to Observe",
  not_meeting: "Not Meeting Expectations",
  meets_expectations: "Meets Expectations",
  impressive: "Impressive",
  excellent: "Excellent",
};

const RATING_BUCKET_COLOR = {
  need_more_time: "default",
  not_meeting: "error",
  meets_expectations: "processing",
  impressive: "success",
  excellent: "gold",
};

const TalentFeedbackModal = ({ open, onClose, onSuccess, hiringRecordId, talentName }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [feedbackType, setFeedbackType] = useState("instant");
  const [instantSubType, setInstantSubType] = useState("general");
  const [ratingBucket, setRatingBucket] = useState(null);

  const handleClose = () => {
    form.resetFields();
    setFeedbackType("instant");
    setInstantSubType("general");
    setRatingBucket(null);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const token = localStorage.getItem("authToken");

      const payload = {
        hiringRecordId,
        feedbackType: values.feedbackType,
        ...values,
      };

      if (values.feedbackType === "monthly" && values.reviewMonth) {
        payload.month = values.reviewMonth.month() + 1;
        payload.year = values.reviewMonth.year();
        delete payload.reviewMonth;
      }

      if (values.ratingBucket) {
        payload.overallScore = RATING_BUCKET_SCORE[values.ratingBucket];
      }

      await axios.post(API_CONST.PERFORMANCE_FEEDBACK, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      message.success("Feedback submitted successfully");
      handleClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      if (err?.errorFields) return;
      const msg = err?.response?.data?.message || "Failed to submit feedback";
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const overallScore = ratingBucket ? RATING_BUCKET_SCORE[ratingBucket] : null;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <MessageOutlined style={{ color: "#014c75" }} />
          <span style={{ color: "#014c75", fontWeight: 700 }}>
            Submit Feedback{talentName ? ` — ${talentName}` : ""}
          </span>
        </div>
      }
      footer={null}
      width={680}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ feedbackType: "instant", instantSubType: "general", severity: "low" }}
        onValuesChange={(changed) => {
          if (changed.feedbackType) { setFeedbackType(changed.feedbackType); }
          if (changed.instantSubType) { setInstantSubType(changed.instantSubType); }
          if (changed.ratingBucket) { setRatingBucket(changed.ratingBucket); }
        }}
      >
        <Form.Item name="feedbackType" label="Feedback Type" rules={[{ required: true }]}>
          <Radio.Group buttonStyle="solid">
            <Radio.Button value="instant"><ExclamationCircleOutlined /> Instant</Radio.Button>
            <Radio.Button value="monthly"><StarOutlined /> Monthly Review</Radio.Button>
            <Radio.Button value="contract_end"><WarningOutlined /> Contract End / Renewal</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {feedbackType === "instant" && (
          <>
            <Form.Item name="instantSubType" label="Category" rules={[{ required: true }]}>
              <Select>
                <Option value="concern">Concern</Option>
                <Option value="warning">Warning</Option>
                <Option value="behavioral_recognition">Behavioral / Recognition</Option>
                <Option value="general">General</Option>
              </Select>
            </Form.Item>
            {(instantSubType === "concern" || instantSubType === "warning") && (
              <Form.Item name="severity" label="Severity">
                <Radio.Group>
                  <Radio.Button value="low">Low</Radio.Button>
                  <Radio.Button value="medium">Medium</Radio.Button>
                  <Radio.Button value="high">High</Radio.Button>
                </Radio.Group>
              </Form.Item>
            )}
            <Form.Item name="title" label="Title" rules={[{ required: true, message: "Please enter a title" }]}>
              <Input placeholder="Brief summary of the feedback" maxLength={255} />
            </Form.Item>
            <Form.Item name="description" label="Details" rules={[{ required: true, message: "Please provide details" }]}>
              <TextArea rows={4} placeholder="Describe the issue or observation in detail" maxLength={2000} showCount />
            </Form.Item>
          </>
        )}

        {(feedbackType === "monthly" || feedbackType === "contract_end") && (
          <>
            {feedbackType === "monthly" && (
              <Form.Item name="reviewMonth" label="Review Month" rules={[{ required: true, message: "Select the month" }]}>
                <DatePicker picker="month" style={{ width: "100%" }} disabledDate={d => d && d > dayjs()} />
              </Form.Item>
            )}

            <Divider orientation="left" plain style={{ fontSize: 13, color: "#888" }}>Performance Ratings (1 = Poor, 5 = Excellent)</Divider>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
              {[
                { name: "ratingTechnical", label: "Technical Skills" },
                { name: "ratingCommunication", label: "Communication" },
                { name: "ratingDelivery", label: "Delivery / Output" },
                { name: "ratingAttitude", label: "Attitude / Teamwork" },
              ].map(({ name, label }) => (
                <Form.Item key={name} name={name} label={label}>
                  <Rate allowHalf count={5} />
                </Form.Item>
              ))}
            </div>

            <Form.Item name="ratingBucket" label="Overall Performance Rating" rules={[{ required: true, message: "Select overall rating" }]}>
              <Select placeholder="Select overall assessment">
                {Object.entries(RATING_BUCKET_LABEL).map(([value, label]) => (
                  <Option key={value} value={value}>
                    <Tag color={RATING_BUCKET_COLOR[value]} style={{ marginRight: 8 }}>{overallScore && value === ratingBucket ? `${overallScore}%` : ""}</Tag>
                    {label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {overallScore !== null && (
              <div style={{ marginBottom: 16, padding: "8px 12px", background: "#f0f9ff", borderRadius: 6, border: "1px solid #bae0ff", fontSize: 13 }}>
                Overall Performance Score: <strong style={{ color: "#014c75", fontSize: 16 }}>{overallScore}%</strong>
                <span style={{ color: "#888", marginLeft: 8 }}>({RATING_BUCKET_LABEL[ratingBucket]})</span>
              </div>
            )}

            <Form.Item name="comments" label="Comments" rules={[{ required: feedbackType === "monthly", message: "Please add comments" }]}>
              <TextArea rows={3} placeholder="Additional observations and notes" maxLength={2000} showCount />
            </Form.Item>
          </>
        )}

        {feedbackType === "contract_end" && (
          <>
            <Divider orientation="left" plain style={{ fontSize: 13, color: "#888" }}>Contract End Evaluation</Divider>
            <Form.Item name="recommendation" label="Overall Recommendation" rules={[{ required: true }]}>
              <Radio.Group>
                <Radio.Button value="recommend">✅ Recommend for Future</Radio.Button>
                <Radio.Button value="neutral">➖ Neutral</Radio.Button>
                <Radio.Button value="do_not_recommend">❌ Do Not Recommend</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item name="wouldRehire" label="Would You Rehire?">
              <Radio.Group>
                <Radio.Button value="yes">Yes</Radio.Button>
                <Radio.Button value="maybe">Maybe</Radio.Button>
                <Radio.Button value="no">No</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item name="coreStrengths" label="Core Strengths">
              <TextArea rows={2} placeholder="Key strengths observed during the contract" maxLength={1000} />
            </Form.Item>
            <Form.Item name="areasOfImprovement" label="Areas of Improvement">
              <TextArea rows={2} placeholder="Areas where improvement is needed" maxLength={1000} />
            </Form.Item>
            <Form.Item name="keyAchievements" label="Key Achievements">
              <TextArea rows={2} placeholder="Notable achievements or contributions" maxLength={1000} />
            </Form.Item>
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit} style={{ background: "#014c75" }}>
            Submit Feedback
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default TalentFeedbackModal;
